import { useOutletContext } from 'react-router-dom'

import { Box, Button, Divider, Text } from '@factorialco/deprecated-design-system'
import { AtsMessageInboxConversationViewPageDocument } from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ApplicationPhaseTag } from 'modules/ats/components/ApplicationPhaseTag'
import { MessageBoard } from 'modules/ats/components/MessageBoard'
import { NoConversationsView } from 'modules/ats/components/NoConversationsView'
import { useMarkAsReadConversation } from 'modules/ats/hooks/conversations/useMarkAsReadConversation'
import { isConversationRead } from 'modules/ats/lib/utils/conversation'

import { MarkAsReadButton } from './components/MarkAsReadButton'
import { MarkAsUnreadButton } from './components/MarkAsUnreadButton'
import { MessageInboxConversationViewSkeleton } from './components/MessageInboxConversationViewSkeleton'

interface OutletContext {
  conversationId: string
}

const MessageInboxConversationViewPage = () => {
  const { access, company } = useCurrentSession()
  const accessId = access.id
  const { navigation } = useNavigation()
  const { conversationId } = useOutletContext<OutletContext>()
  const { data: conversationData, loading: loadingConversation } = useQuery(
    AtsMessageInboxConversationViewPageDocument,
    {
      variables: { conversationId },
    }
  )

  const conversation = conversationData?.ats.conversationsConnection.nodes[0]

  const { markAsReadConversation } = useMarkAsReadConversation()
  const statusRead = conversation && isConversationRead(conversation)

  useEffect(() => {
    markAsReadConversation({
      id: String(conversationId),
      accessId: access.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  if (loadingConversation) {
    return <MessageInboxConversationViewSkeleton />
  }

  if (!conversationId) return <NoConversationsView />

  if (!conversation || !conversation.application.id) {
    return <NoConversationsView error />
  }

  return (
    <Box width="full" maxHeight="full" style={{ flex: '1 1 auto' }}>
      <Box>
        <Box
          width="full"
          flexDirection="row"
          paddingX="s32"
          paddingBottom="s16"
          alignItems="center"
          gap="s8"
          justifyContent="spaceBetween"
        >
          <Box flexGrow flexDirection="row" gap="s16">
            <Text.Normal weight="medium" color="grey1000">
              {conversation.application.candidate.fullName}
            </Text.Normal>
            <ApplicationPhaseTag applicationPhase={conversation.application.applicationPhase} />
          </Box>
          {statusRead ? (
            <MarkAsUnreadButton conversationId={conversation.id} accessId={accessId} />
          ) : (
            <MarkAsReadButton conversationId={conversation.id} accessId={accessId} />
          )}
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="center">
              <PopoverMenu.RouterOption
                title={i18n.t('ats.messages.application_details_button')}
                to={{
                  pathname: navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
                    jobId: conversation.application.jobPosting.id,
                    application_id: conversation.application.id,
                  }),
                }}
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
        <Divider />
      </Box>

      <Box paddingX="s24" style={{ flex: '1 1 auto', overflowY: 'scroll' }}>
        <MessageBoard conversation={conversation} companyId={company.id} />
      </Box>
    </Box>
  )
}

export default MessageInboxConversationViewPage
